/**
 * MetricContributionsTable — per-formula-group contribution rows.
 *
 * Sliders mode: shows `metric weight × value = contribution` rows so the
 * user can see exactly which metric pushed the score up.
 * Blocks mode (no `contributions` payload): renders nothing; the breakdown
 * list already shows the expression and metric values.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { cn } from '@/lib/utils';

import { HoverSection } from './hover-primitives';
import {
  fmtScore,
  fmtStatValue,
  metricLabel,
  metricTone,
  rawScoreTone,
} from '../format';
import type { PreviewBreakdownEntry } from '../../preview-row.types';

export interface MetricContributionsTableProps {
  entries: PreviewBreakdownEntry[];
}

const MetricContributionsTable: FC<MetricContributionsTableProps> = ({
  entries,
}) => {
  const groupsWithContributions = entries.filter(
    (e) => e.contributions && e.contributions.length > 0,
  );
  if (groupsWithContributions.length === 0) return null;
  return (
    <HoverSection label="Per-metric contributions">
      <div className="space-y-1.5">
        {groupsWithContributions.map((entry) => (
          <GroupContributions key={entry.groupId} entry={entry} />
        ))}
      </div>
    </HoverSection>
  );
};

const GroupContributions: FC<{ entry: PreviewBreakdownEntry }> = ({ entry }) => (
  <div className="rounded border border-border/60 px-2 py-1.5">
    <div className="mb-1 flex items-baseline justify-between gap-2 text-[10px]">
      <span className="truncate font-medium text-foreground">
        {entry.groupName}
      </span>
      <span className="text-muted-foreground">
        total{' '}
        <span className={cn('font-medium tabular-nums', rawScoreTone(entry.rawScore))}>
          {fmtScore(entry.rawScore)}
        </span>
      </span>
    </div>
    <ul className="space-y-0.5">
      {(entry.contributions ?? []).map((c) => (
        <ContributionRow key={c.metric} row={c} />
      ))}
    </ul>
  </div>
);

const ContributionRow: FC<{
  row: NonNullable<PreviewBreakdownEntry['contributions']>[number];
}> = ({ row }) => (
  <li className="grid grid-cols-[1fr_auto_auto_auto] items-baseline gap-2 text-[10px] tabular-nums">
    <span className="truncate text-muted-foreground">
      {metricLabel(row.metric)}
    </span>
    <span className="text-muted-foreground">{row.weight}%</span>
    <span className={cn(metricTone(row.metric, row.value))}>
      {fmtStatValue(row.metric, row.value)}
    </span>
    <span className={cn('font-medium', rawScoreTone(row.contribution))}>
      = {fmtScore(row.contribution)}
    </span>
  </li>
);

export default MetricContributionsTable;
